import React, { useState, useEffect } from "react";

import { Col, Row, Card, Button, Form, notification, message, Tooltip, Tabs, Spin, Table, Modal, Input } from "antd";
import { LockOutlined, PlusOutlined, SaveFilled, AppstoreFilled } from "@ant-design/icons";

import { useNavigate, useLocation } from "react-router-dom";

import { getUsers, getUserById, createUser, updateUser, deleteUser } from "../../API/users";
import { getRoles, deleteRole } from "../../API/roles";
import { adminPasswordReset } from "../../API/password";
import { getControlsArea } from "../../API/controls";
import { getLicences } from "../../API/licences";

import UserSearch from "./components/UserSearch/UserSearch";
import UserColumns from './components/UserTable/columns';
import UserModal from "./components/UserModal/UserModal";
import RoleSearch from "./components/RoleSearch/RoleSearch";
import RoleColumns from './components/RoleTable/columns';

import WithPermission from "../Common/WithPermission/WithPermission";
import ChangeLog from "../Common/ChangeLog/ChangeLog";
import ExportButton from '../Common/ExportButton/ExportButton';
import PageTitle from '../Common/PageTitle/PageTitle';
import Assetia from '../Common/Assetia/Assetia';
import dayjs from 'dayjs';

import { useAuth } from "../Context/AuthContext";

import "./UserManagement.css";

const { TabPane } = Tabs;

const UserManagement = () => {
  // Permisos
  const { userData } = useAuth();
  // Consulta permisos: Superusuario  
  const isSuperuser = userData?.is_superuser === true;

  const [api, contextHolder] = notification.useNotification();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  // Usuario
  const [users, setUsers] = useState([]);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  // Roles
  const [roles, setRoles] = useState([]);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [editingRole, setEditingRole] = useState(null);
  // Areas
  const [areas, setAreas] = useState([]);
  // User roles (Modal)
  const [userRoles, setUserRoles] = useState([]);
  // Loader
  const [loading, setLoading] = useState(true);
  // Buscador y data
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [searchUserText, setSearchUserText] = useState('');
  const [searchRoleText, setSearchRoleText] = useState('');
  // Tab
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || "1");
  // Max Assets
  const [maxUsersAllowed, setMaxUsersAllowed] = useState(null);

  // USUARIOS

  // Cargar lista de usuarios
  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await getUsers();
      setUsers(response);
      setFilteredUsers(response);
    } catch {
      api.error({ message: "Error al cargar los usuarios"});
    } finally {
      setLoading(false);
    }
  };

  // Obtener licencia
  const fetchLicence = async () => {
    try {
      const response = await getLicences();
      // Encontramos la licencia activa (active: true)
      const activeLicence = response?.find(lic => lic.active);

      if (activeLicence?.product) {
        setMaxUsersAllowed(activeLicence.product.number_of_users || null);
      }
    } catch (error) {
      api.error({ message: "Error al obtener la licencia activa." });
    }
  };

  // Obtener Áreas
  /* const fetchAreas = async () => {
    try {
      const response = await getControlsArea();
      setAreas(response);
    } catch (error) {
      api.error({ message: "Error al cargar las áreas." });
    }
  }; */

  // Crear/Actualizar usuario
  const handleUserSave = async (values) => {
    try {
      if (editingUser) {
        await updateUser(editingUser.id, values);
        api.success({ message: "Usuario actualizado."});
      } else {
        await createUser(values);
        api.success({ message: "Usuario agregado."});
      }
      setIsUserModalOpen(false);
      form.resetFields();
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      const errData = error?.response?.data;

      let description = "Ocurrió un error inesperado al guardar el usuario.";

      if (errData?.name?.[0]?.includes("already exists")) {
        description = "Ya existe un usuario con ese nombre.";
      } else if (errData?.email?.[0]?.includes("Ya existe un usuario")) {
        description = "Ya existe un usuario con este correo electrónico.";
      } else if (errData?.username?.[0]?.includes("already exists")) {
        description = "Ya existe un usuario con ese nombre de usuario.";
      }

      api.error({ 
        message: "Error al guardar el usuario.",
        description,
        key: 'save-users-error'
      });
    }
  };

  // Eliminar un usuario
  const handleUserDelete = async (id) => {
    try {
      await deleteUser(id);
      api.success({ message: "Usuario eliminado."});
      fetchUsers();
    } catch {
      api.error({ message: "Error al eliminar el usuario."});
    }
  }; 

  // Restablecer contraseña
  const handlePasswordReset = async (user) => {
    try {
      const email = user.email;
      const { response, error } = await adminPasswordReset(email);
      
      if (response) {
        api.success({
          message: "Solicitud enviada",
          description: `Se ha enviado un correo a ${user.email} con instrucciones para restablecer la contraseña.`
        });
      } else {
        api.error({
          message: "Error al enviar solicitud",
          description: error?.message || "No se pudo enviar el correo de restablecimiento."
        });
      }
    } catch (err) {
      api.error({ 
        message: "Error al intentar restablecer la contraseña",
        description: "Por favor intente nuevamente."
      });
    }
  };
  
  useEffect(() => {
    fetchUsers();
    fetchRoles();
    fetchLicence();
  }, []);
  
  // Mostrar modal para agregar/editar usuarios
  const showUserModal = async (asset = null) => {
    try {
      const [areasResponse, rolesResponse] = await Promise.all([getControlsArea(), getRoles()]);
      setAreas(areasResponse);
      setUserRoles(rolesResponse);
    } catch (error) {
      api.error({ message: "Error al cargar áreas o roles." });
    }

    setEditingUser(asset);
    if (asset) {
      form.setFieldsValue({
        first_name: asset.first_name,
        last_name: asset.last_name,
        username: asset.username,
        email: asset.email,
        area_id: asset?.area?.id || null,
        role_id: asset?.roles?.[0]?.id || null,
        is_active: asset?.is_active ?? true,
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        is_active: true,
      });
    }
    setIsUserModalOpen(true);
  };

  const userTableFilter = () => {
    const uniqueUsers = Array.from(
      new Set(users.map(a => a.user?.username).filter(Boolean))).map(username => ({ username })); 
  
    return { users: uniqueUsers };
  };

  // ROLES

  // Cargar lista de roles
  const fetchRoles = async () => {
    setLoading(true);
    try {
      const response = await getRoles();
      setRoles(response);
      setFilteredRoles(response); 
    } catch {
      api.error({ message: "Error al cargar los roles"});
    } finally {
      setLoading(false);
    }
  };

  /* // Crear/Actualizar roles
  const handleRoleSave = async (values) => {
    try {
      if (editingRole) {
        await updateRole(editingRole.code, values);
        api.success({ message: "Rol actualizado."});
      } else {
        await createRole(values);
        api.success({ message: "Rol agregado."});
      }
      setIsUserModalOpen(false);
      form.resetFields();
      setEditingRole(null);
      fetchUsers();
    } catch (error) {
      api.error({ 
        message: "Error al guardar el rol.",
        description: error?.response?.data?.name?.[0].includes("already exists") 
          ? "Ya existe un rol con ese nombre."
          : "Ocurrió un error inesperado al guardar el rol.",
        key: 'save-users-error'
      });
    }
  }; */

  // Eliminar un roles
  const handleRoleDelete = async (id) => {
    try {
      await deleteRole(id);
      api.success({ message: "Rol eliminado."});
      fetchRoles();
    } catch {
      api.error({ message: "Error al eliminar el rol."});
    }
  }; 
  
  const handleCreateRole = () => {
    navigate("/user-management/roles/create");
  };

  const handleEditRole = (role) => {
    navigate(`/user-management/roles/${role.id}`);
  };

  /* const roleTableFilter = () => {
    const uniqueUsers = Array.from(
      new Set(users.map(a => a.user?.username).filter(Boolean))).map(username => ({ username })); 
  
    return { users: uniqueUsers };
  }; */

  // Modal + Log
  const [changeLogVisible, setChangeLogVisible] = useState(false);
  const [changeLogData, setChangeLogData] = useState(null);
  const [changeLogLogs, setChangeLogLogs] = useState([]);

  const handleOpenChangeLog = async (record) => {
    try {
      const evaluation = await getUserById(record.id);
      setChangeLogData(record);
      setChangeLogLogs(evaluation.logs || []);
      setChangeLogVisible(true); 
    } catch (error) {
      api.error({ message: "Error al obtener logs" });
    }
  };

  const handleCloseChangeLog = () => {
    setChangeLogVisible(false);
    setTimeout(() => {
      setChangeLogData(null);
      setChangeLogLogs([]);
    }, 300);
  };

  return (
    <WithPermission
      isSuperuser={isSuperuser}
      requiredPermission="read"
      redirectTo="/home"
    >
      <>
        {contextHolder}
        <div className="button-container">
          <Row justify="start">
            <Col>
              <PageTitle title="Gestión de Usuarios" icon="usermanagement" />
            </Col>
          </Row>

          <Tabs
            className="user-management"
            centered
            activeKey={activeTab}
            onChange={(key) => setActiveTab(key)}
          >
            <TabPane tab="Usuarios" key="1">
              <Row justify="start" gutter={[0, 16]}>
                <Col span={16}>
                  <Tooltip
                    placement="top"
                    title={
                      (maxUsersAllowed && users.length >= maxUsersAllowed)
                        ? `Alcanzó el limite máximo de ${maxUsersAllowed} usuarios  de su Licencia`
                        : !isSuperuser
                          ? "No tiene permisos para agregar usuarios"
                          : ""
                    }
                  >
                    <span style={{ display: 'inline-block' }}>
                      <Button
                        className="add-button"
                        onClick={() => showUserModal()}
                        loading={loading}
                        disabled={
                          (!isSuperuser) ||
                          (maxUsersAllowed && users.length >= maxUsersAllowed)
                        }
                      >
                        <PlusOutlined />
                        Agregar Usuario
                      </Button>
                    </span>
                  </Tooltip>
                </Col>
                <Col span={8} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <UserSearch
                    usersData={users}
                    searchText={searchUserText}
                    setSearchText={setSearchUserText}
                    setFilteredData={setFilteredUsers}
                  />
                </Col>
              </Row>

              <Row justify="end"  gutter={[8, 0]} style={{ marginBottom: 16 }}>
                {
                  isSuperuser &&
                  <Col>
                    <Button 
                      type="primary"
                      className="view-deleted-button"
                      onClick={() => navigate("/users-deleted")}>
                      <AppstoreFilled />
                      Ver Borrados
                    </Button>
                  </Col>
                }
                <Col>
                  <ExportButton
                    api={api}
                    contextHolder={contextHolder}
                    data={filteredUsers}
                    filename="Usuarios.csv"
                    transformData={(data) =>
                    data.map(item => ({
                      Usuario: item.username,
                      Nombre: item.first_name,
                      Apellido: item.last_name,
                      Email: item.email,
                      Área: item.area.name,
                      'Rol Asignado': item.roles?.[0]?.name || '-',
                      'Último Inicio Sesión': item.last_login_app 
                        ? dayjs(item.last_login_app).format('DD/MM/YYYY HH:mm') 
                        : '-',
                      Estado: item.is_active ? 'Activo' : 'Inactivo'
                      }))
                    }
                  />
                </Col>
              </Row>

              <Spin className="custom-spin" spinning={loading}>
                <Table
                  className="custom-pagination"
                  columns={UserColumns(showUserModal, handleUserDelete, userTableFilter(), handleOpenChangeLog, handlePasswordReset)} 
                  dataSource={filteredUsers}
                  scroll={{ y: 450 }}
                  rowKey="code"
                  pagination={{
                    defaultPageSize: 30,
                    showSizeChanger: true,
                    pageSizeOptions: ['30', '50', '100'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`
                  }}
                />

                <ChangeLog
                  open={changeLogVisible}
                  onClose={handleCloseChangeLog}
                  data={changeLogData}
                  logs={changeLogLogs}
                />
              </Spin>
            </TabPane>

            <TabPane tab="Roles" key="2">
              <Row justify="start" gutter={[0, 16]}>
                <Col span={16}>
                  <Button className="add-button" onClick={handleCreateRole}>
                    <PlusOutlined />
                    Agregar Roles
                  </Button>
                </Col>
                <Col span={8} style={{ display: 'flex', justifyContent: 'flex-end' }}>
                  <RoleSearch
                    roleData={roles}
                    searchText={searchRoleText}
                    setSearchText={setSearchRoleText}
                    setFilteredData={setFilteredRoles}
                  />  
                </Col>
              </Row>

              <Row justify="end"  gutter={[8, 0]} style={{ marginBottom: 16 }}>
                {
                  isSuperuser &&
                  <Col>
                    <Button 
                      type="primary"
                      className="view-deleted-button"
                      onClick={() => navigate("/roles-deleted")}>
                      <AppstoreFilled />
                      Ver Borrados
                    </Button>
                  </Col>
                }
                <Col>
                  <ExportButton
                    api={api}
                    contextHolder={contextHolder}
                    data={filteredRoles}
                    filename="Roles.csv"
                    transformData={(data) =>
                    data.map(item => ({
                      'Nombre del Rol': item.name,
                      Descripción: item.description,
                      }))
                    }
                  />
                </Col>
              </Row>
              <Spin className="custom-spin" spinning={loading}>
                <Table
                  className="custom-pagination"
                  columns={RoleColumns(handleEditRole, handleRoleDelete, users, api)}
                  dataSource={filteredRoles} 
                  scroll={{ y: 450 }}
                  rowKey="code"
                  pagination={{
                    defaultPageSize: 30,
                    showSizeChanger: true,
                    pageSizeOptions: ['30', '50', '100'],
                    showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} registros`
                  }}
                />
              </Spin>
            </TabPane>
          </Tabs>
        </div>

        {/* Modal para Agregar/Editar Usuarios */}
        <UserModal
          isOpen={isUserModalOpen}
          onCancel={() => setIsUserModalOpen(false)}
          onSubmit={handleUserSave}
          form={form}
          editingUser={editingUser}
          areas={areas}
          userRoles={userRoles}
        />

        <Row style={{ marginTop: 80 }}>
          <Assetia 
            pageTitle="Seguridad" 
            pageDescription="cargar tus activos y gestionarlos de manera simple"
            tips={[
            "Carga tus activos para tener un control claro y actualizado en todo momento.",
            "Organiza los activos por categorías para facilitar su búsqueda y gestión.",
            "Revisa el estado de tus activos para anticipar posibles fallas o mantenimientos."
            ]}        
          />
        </Row>
      </>
    </WithPermission>
  );
};

export default UserManagement;